import "./profiles.js";
//#region src/auth_selection.ts
/**
* # Which credential an invocation authenticates with
*
* Four inputs can each answer "who am I": the `--api-key` flag, `NEON_API_KEY`, the
* `--profile` flag, and `NEON_PROFILE`. This module decides between them, and it is pure so
* the decision can be tested without a filesystem, a network, or a config directory.
*
* ## The rule
*
* **An explicit flag beats an ambient environment variable.** That single rule fixes the bug
* this module exists for: before it, any API key — including one merely exported into the
* shell — silently voided `--profile`, so `neon --profile work …` would quietly run as
* whoever `NEON_API_KEY` belonged to and say nothing about it.
*
* | Given | What runs |
* | --- | --- |
* | `--api-key` and `--profile` | neither: contradictory explicit flags, so this throws |
* | `--api-key` and `NEON_PROFILE` | the flag's key |
* | `--profile` and `NEON_API_KEY` | the profile |
* | `NEON_API_KEY` and `NEON_PROFILE` | the key, and the ignored profile is named in a warning |
* | `--profile` or `NEON_PROFILE` alone | that profile |
* | nothing | `DEFAULT` |
*
* Two explicit flags throw rather than picking a winner. They express different intents —
* `--api-key` supplies a credential, `--profile` selects a stored one — so there is no
* reading of the command that makes both true, and guessing is how the original bug behaved.
*
* When both are merely ambient, the key wins. That keeps CI exactly as it was: a pipeline
* that injects `NEON_API_KEY` must not change behaviour because a `NEON_PROFILE` leaked into
* the environment. It warns instead of staying silent, because a disregarded account
* selection is precisely what nobody noticed last time.
*
* `auth` and the `profile` subcommands do not use any of this. They read the same flags with
* different meanings — `neon auth --profile work` names where to *write* a credential, and
* `neon profile create work --api-key …` names one to *store* — so their callers skip
* selection entirely rather than passing exemptions down here.
*/
let inputs = {
	apiKeyFlag: "",
	apiKeyEnv: "",
	profileEnv: "",
	profileFlag: "",
	configDir: ""
};
const recordCredentialInputs = (recorded) => {
	inputs = recorded;
};
const credentialInputs = () => inputs;
const selectCredential = ({ apiKeyFlag, profileFlag, apiKeyEnv, profileEnv }) => {
	const flagKey = nonEmpty(apiKeyFlag);
	const flagProfile = nonEmpty(profileFlag);
	if (flagKey !== void 0 && flagProfile !== void 0) throw new Error("Pass either --api-key or --profile, not both. --api-key supplies a credential directly; --profile selects a stored one.");
	if (flagKey !== void 0) return {
		source: "explicit-api-key",
		apiKey: flagKey
	};
	if (flagProfile !== void 0) return {
		source: "profile",
		profile: flagProfile,
		explicit: true
	};
	const envKey = nonEmpty(apiKeyEnv);
	const envProfile = nonEmpty(profileEnv);
	if (envKey !== void 0) return {
		source: "ambient-api-key",
		apiKey: envKey,
		...envProfile !== void 0 ? { ignoredProfile: envProfile } : {}
	};
	return {
		source: "profile",
		profile: envProfile ?? "DEFAULT",
		explicit: envProfile !== void 0
	};
};
/** The warning for an ambient key that displaced an ambient profile, or `null`. */
const displacedProfileWarning = (selection) => selection.source === "ambient-api-key" && selection.ignoredProfile !== void 0 ? `NEON_API_KEY is set, so profile "${selection.ignoredProfile}" from NEON_PROFILE was ignored. Pass --profile ${selection.ignoredProfile} to use it instead.` : null;
function nonEmpty(value) {
	if (typeof value !== "string") return void 0;
	const trimmed = value.trim();
	return trimmed === "" ? void 0 : trimmed;
}
//#endregion
export { credentialInputs, displacedProfileWarning, recordCredentialInputs, selectCredential };

//# sourceMappingURL=auth_selection.js.map